<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import CrmAPI from 'dashboard/api/crm';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

// inline: renderiza como PÁGINA em vez de modal flutuante
// only: mostra só uma seção ('n8n' | 'meta' | 'google' | 'ai') — usado
// pelos cards "Configurar" da página de Integrações
const props = defineProps({
  inline: { type: Boolean, default: false },
  only: { type: String, default: null },
});

const showSection = s => !props.only || props.only === s;

const emit = defineEmits(['close']);

const store = useStore();
const settings  = useMapGetter('crm/getSettings');
const workflows = useMapGetter('crm/getN8nWorkflows');

const n8nUrl    = ref('');
const n8nApiKey = ref('');
const isSaving  = ref(false);
const isTesting = ref(false);
const isFetching = ref(false);
const testResult = ref(null);   // { success, message }

// ── Meta (Facebook/Instagram Ads) ──────────────────────────
const meta = ref({ pixel_id: '', access_token: '', ad_account_id: '', test_event_code: '' });
const isMetaSaving = ref(false);
const isMetaTesting = ref(false);
const metaTestResult = ref(null);
const showMetaRequirements = ref(false);

// ── Claude (IA nativa) ─────────────────────────────────────
const ai = ref({ api_key: '', model: '', effort: '' });
const isAiSaving = ref(false);
const isAiTesting = ref(false);
const aiTestResult = ref(null);

const testAi = async () => {
  isAiTesting.value = true;
  aiTestResult.value = null;
  try {
    const { data } = await CrmAPI.testAi();
    aiTestResult.value = data;
  } catch {
    aiTestResult.value = { success: false, message: 'Erro ao testar a conexão.' };
  } finally {
    isAiTesting.value = false;
  }
};

const AI_MODELS = [
  { value: 'claude-opus-4-8', label: 'Claude Opus 4.8 — melhor análise (padrão)' },
  { value: 'claude-sonnet-5', label: 'Claude Sonnet 5 — equilíbrio' },
  { value: 'claude-haiku-4-5', label: 'Claude Haiku 4.5 — mais barato e rápido' },
];

// Esforço = quanto a IA "pensa" antes de responder (mais esforço =
// análise melhor e um pouco mais cara/lenta). O Haiku ignora este campo.
const AI_EFFORTS = [
  { value: 'low', label: 'Baixo — rápido e econômico' },
  { value: 'medium', label: 'Médio — equilíbrio' },
  { value: 'high', label: 'Alto — padrão recomendado' },
  { value: 'max', label: 'Máximo — melhor análise possível' },
];

// ── Gemini (Google) — integração nativa: chave p/ gerar IMAGENS nas Páginas ──
const gemini = ref({ api_key: '' });
const isGeminiSaving = ref(false);
const isGeminiTesting = ref(false);
const geminiTestResult = ref(null);

const saveGemini = async () => {
  if (!gemini.value.api_key.trim()) {
    useAlert('Cole a chave do Gemini para salvar.');
    return;
  }
  isGeminiSaving.value = true;
  try {
    await CrmAPI.updateAi({ gemini_api_key: gemini.value.api_key.trim() });
    gemini.value.api_key = '';
    await store.dispatch('crm/fetchSettings');
    useAlert('Chave do Gemini salva!');
  } catch (error) {
    useAlert(error?.response?.data?.error || 'Erro ao salvar a chave do Gemini.');
  } finally {
    isGeminiSaving.value = false;
  }
};

const testGeminiConnection = async () => {
  isGeminiTesting.value = true;
  geminiTestResult.value = null;
  try {
    const { data } = await CrmAPI.testGemini();
    geminiTestResult.value = data;
  } catch {
    geminiTestResult.value = { success: false, message: 'Erro ao testar a conexão.' };
  } finally {
    isGeminiTesting.value = false;
  }
};

const saveAi = async () => {
  isAiSaving.value = true;
  try {
    const payload = { model: ai.value.model, effort: ai.value.effort };
    if (ai.value.api_key.trim()) payload.api_key = ai.value.api_key.trim();
    await CrmAPI.updateAi(payload);
    ai.value.api_key = '';
    await store.dispatch('crm/fetchSettings');
    useAlert('Configurações de IA salvas');
  } catch (error) {
    useAlert(error?.response?.data?.error || 'Erro ao salvar configurações de IA.');
  } finally {
    isAiSaving.value = false;
  }
};

// ── Google Sheets (planilha de cirurgias) ──────────────────
const sheets = ref({ sheet_url: '' });
const isSheetsSaving = ref(false);
const isSheetsTesting = ref(false);
const sheetsTestResult = ref(null);
const sheetsPreview = ref(null);

const saveSheets = async () => {
  isSheetsSaving.value = true;
  sheetsTestResult.value = null;
  sheetsPreview.value = null;
  try {
    await CrmAPI.updateSheets({ sheet_url: sheets.value.sheet_url.trim() });
    await store.dispatch('crm/fetchSettings');
    useAlert('Planilha salva');
  } catch {
    useAlert('Erro ao salvar a planilha.');
  } finally {
    isSheetsSaving.value = false;
  }
};

const testSheets = async () => {
  isSheetsTesting.value = true;
  sheetsTestResult.value = null;
  sheetsPreview.value = null;
  try {
    const { data } = await CrmAPI.testSheets();
    sheetsTestResult.value = data;
    if (data.success) sheetsPreview.value = data.preview || [];
    await store.dispatch('crm/fetchSettings');
  } catch {
    sheetsTestResult.value = { success: false, message: 'Erro ao testar a planilha.' };
  } finally {
    isSheetsTesting.value = false;
  }
};

// ── Google ─────────────────────────────────────────────────
// ga4_property_id + service_account_json (item 155): a LEITURA (cliques,
// custo, palavras-chave, investimento automático) usa a GA4 Data API —
// esses 2 campos só existiam na tela antiga e o aviso apontava pra cá
const google = ref({ measurement_id: '', api_secret: '', client_id: '', developer_token: '', customer_id: '', ga4_property_id: '', service_account_json: '', search_console_site: '',
  // 🎯 item 371: conversões direto no Google Ads (gclid)
  login_customer_id: '', oauth_client_id: '', oauth_client_secret: '', oauth_refresh_token: '', ads_impersonate_email: '', conversions_via: 'ga4' });
const isAdsPreparing = ref(false);
const isAdsTesting = ref(false);
const adsResult = ref(null);
const showAdsHelp = ref(false);
const isGoogleSaving = ref(false);
const isGoogleTesting = ref(false);
const googleTestResult = ref(null);
const showGoogleRequirements = ref(false);
// 🔎 16/10: Search Console (mesma conta de serviço; teste à parte)
const isGscTesting = ref(false);
const gscTestResult = ref(null);

onMounted(async () => {
  await store.dispatch('crm/fetchSettings');
  n8nUrl.value    = settings.value.n8n_base_url || '';
  n8nApiKey.value = '';  // nunca pré-preenche a key por segurança
  oftalmo.value.base_url = settings.value.oftalmofacil?.base_url || '';
  const of = settings.value.oftalmofacil || {};
  oftalmo.value = {
    ...oftalmo.value,
    db_host: of.db_host || '', db_port: of.db_port || 3306, db_name: of.db_name || '', db_user: of.db_user || '',
    db_password: '', provider_name: of.provider_name || 'CATARATA_SP', enabled: !!of.enabled,
    // 🏥 item 228: hub de parceiros + Agenda unificada
    partners_enabled: !!of.partners_enabled, partner_pipeline_id: of.partner_pipeline_id || null,
    own_pipeline_id: of.own_pipeline_id || null, agenda_enabled: !!of.agenda_enabled, agenda_from: of.agenda_from || '',
    partner_inbox_ids: (of.partner_inbox_ids || []).map(Number), // 🚧 item 231
  };
  oftalmoDoctors.value = Object.entries(of.doctors || {}).map(([crm, name]) => ({ crm, name }));
  oftalmoClinics.value = Object.entries(of.clinics || {}).map(([name, unit]) => ({ name, unit }));
  if (!crmPipelines.value.length) store.dispatch('crm/fetchPipelines').catch(() => {});

  const m = settings.value.meta_ads || {};
  meta.value = {
    pixel_id: m.pixel_id || '',
    access_token: '', // nunca pré-preenche
    ad_account_id: m.ad_account_id || '',
    test_event_code: m.test_event_code || '',
  };

  const a = settings.value.ai || {};
  ai.value = { api_key: '', model: a.model || 'claude-opus-4-8', effort: a.effort || 'high' };

  const g = settings.value.google_ads || {};
  google.value = {
    measurement_id: g.measurement_id || '',
    api_secret: '',
    client_id: g.client_id || '',
    developer_token: '',
    customer_id: g.customer_id || '',
    ga4_property_id: g.ga4_property_id || '',
    service_account_json: '', // write-only, nunca volta do servidor
    search_console_site: g.search_console_site || 'sc-domain:cevico.com.br',
    login_customer_id: g.login_customer_id || '',
    oauth_client_id: g.oauth_client_id || '',
    oauth_client_secret: '', // write-only
    oauth_refresh_token: '', // write-only
    ads_impersonate_email: g.ads_impersonate_email || '',
    conversions_via: g.conversions_via || 'ga4',
  };

  sheets.value = { sheet_url: settings.value.sheets?.sheet_url || '' };
});

const metaStatus = computed(() => settings.value.meta_ads || {});
const googleStatus = computed(() => settings.value.google_ads || {});
const aiStatus = computed(() => settings.value.ai || {});
const sheetsStatus = computed(() => settings.value.sheets || {});

// ── OftalmoFácil (conexão nativa) ──────────────────────────
// item 157: a conexão real é o BANCO (usuário só-leitura) + fornecedor + de-para de médicos
const oftalmo = ref({
  base_url: '', api_key: '', db_host: '', db_port: 3306, db_name: '', db_user: '', db_password: '', provider_name: 'CATARATA_SP', enabled: false,
  partners_enabled: false, partner_pipeline_id: null, own_pipeline_id: null, agenda_enabled: false, agenda_from: '', partner_inbox_ids: [],
});
const oftalmoDoctors = ref([]); // [{ crm, name }]
// 🏥 item 228: de-para clínica do OftalmoFácil → unidade/local da Agenda
const oftalmoClinics = ref([]); // [{ name, unit }]
const addOftalmoClinic = (name = '') => oftalmoClinics.value.push({ name, unit: '' });
const crmPipelines = useMapGetter('crm/getPipelines');
const allInboxes = useMapGetter('inboxes/getInboxes'); // 🚧 item 231: caixa(s) dos parceiros
const oftalmoUnitOptions = computed(() => {
  const base = [
    { key: 'paulista', label: 'Av. Paulista (consultas/exames)' },
    { key: 'tatuape', label: 'Tatuapé (consultas/exames)' },
  ];
  const locs = Array.isArray(settings.value.surgery_locations) && settings.value.surgery_locations.length
    ? settings.value.surgery_locations
    : [{ key: 'iop', label: 'IOP' }, { key: 'ocular_surgery', label: 'Ocular Surgery' }];
  return base.concat(locs.map(l => ({ key: l.key, label: `${l.label} (cirurgias)` })));
});
// parceiros/clínicas/tipos vistos: do espelho (já sincronizado) ou do teste de conexão (retrato do hub inteiro)
const oftalmoPartnersSeen = computed(() => oftalmoTest.value?.partners?.length ? oftalmoTest.value.partners : (oftalmoStatus.value.partners_seen || []).map(p => ({ ...p, own: (p.name || '').toLowerCase().includes((oftalmo.value.provider_name || '').toLowerCase()) })));
const oftalmoClinicsSeen = computed(() => oftalmoTest.value?.clinics?.length ? oftalmoTest.value.clinics : (oftalmoStatus.value.clinics_seen || []));
const oftalmoTypesSeen = computed(() => oftalmoTest.value?.procedure_types?.length ? oftalmoTest.value.procedure_types : (oftalmoStatus.value.types_seen || []));
const clinicMapped = name => oftalmoClinics.value.some(c => c.name.trim().toLowerCase() === String(name).trim().toLowerCase());
const isOftalmoTesting = ref(false);
const oftalmoTest = ref(null);
const isOftalmoSyncing = ref(false);
const addOftalmoDoctor = () => oftalmoDoctors.value.push({ crm: '', name: '' });
const testOftalmo = async () => {
  isOftalmoTesting.value = true;
  oftalmoTest.value = null;
  try {
    const { data } = await CrmAPI.testOftalmofacil();
    oftalmoTest.value = data;
  } catch {
    oftalmoTest.value = { ok: false, error: 'Não consegui testar agora.' };
  } finally {
    isOftalmoTesting.value = false;
  }
};
const syncOftalmo = async full => {
  isOftalmoSyncing.value = true;
  try {
    await CrmAPI.syncOftalmofacil(full);
    useAlert(full ? 'Recarga completa iniciada em segundo plano (histórico em silêncio)' : 'Sincronização iniciada em segundo plano');
    setTimeout(() => store.dispatch('crm/fetchSettings').catch(() => {}), 4000);
  } catch (e) {
    // 409 = outra sincronização ainda rodando (o servidor manda o aviso)
    useAlert(e?.response?.data?.error || 'Erro ao iniciar a sincronização.');
  } finally {
    isOftalmoSyncing.value = false;
  }
};
const isOftalmoSaving = ref(false);
const oftalmoStatus = computed(() => settings.value.oftalmofacil || {});

const saveOftalmo = async () => {
  isOftalmoSaving.value = true;
  try {
    await CrmAPI.updateOftalmofacil({
      base_url: oftalmo.value.base_url.trim(),
      api_key: oftalmo.value.api_key.trim(),
      db_host: oftalmo.value.db_host.trim(),
      db_port: Number(oftalmo.value.db_port) || 3306,
      db_name: oftalmo.value.db_name.trim(),
      db_user: oftalmo.value.db_user.trim(),
      db_password: oftalmo.value.db_password,
      provider_name: oftalmo.value.provider_name.trim(),
      enabled: oftalmo.value.enabled,
      doctors: Object.fromEntries(oftalmoDoctors.value.filter(d => d.crm && d.name).map(d => [String(d.crm).replace(/\D/g, ''), d.name.trim()])),
      // 🏥 item 228
      partners_enabled: oftalmo.value.partners_enabled,
      partner_pipeline_id: oftalmo.value.partner_pipeline_id || null,
      own_pipeline_id: oftalmo.value.own_pipeline_id || null,
      // 🧭 item 341: as caixas da cerca agora se escolhem só em "Ambientes do CRM"
      // (este card não manda mais partner_inbox_ids — não sobrescreve a tela nova)
      agenda_enabled: oftalmo.value.agenda_enabled,
      agenda_from: oftalmo.value.agenda_from || null,
      clinics: Object.fromEntries(oftalmoClinics.value.filter(c => c.name.trim() && c.unit).map(c => [c.name.trim(), c.unit])),
    });
    oftalmo.value.api_key = ''; // nunca fica na tela depois de salvar
    await store.dispatch('crm/fetchSettings');
    useAlert('Conexão OftalmoFácil salva. 👁️');
  } catch (error) {
    useAlert(error?.response?.data?.error || 'Erro ao salvar a conexão.');
  } finally {
    isOftalmoSaving.value = false;
  }
};

const saveMeta = async () => {
  isMetaSaving.value = true;
  metaTestResult.value = null;
  try {
    const payload = {};
    if (meta.value.pixel_id.trim()) payload.pixel_id = meta.value.pixel_id.trim();
    if (meta.value.access_token.trim()) payload.access_token = meta.value.access_token.trim();
    payload.ad_account_id = meta.value.ad_account_id.trim();
    payload.test_event_code = meta.value.test_event_code.trim();
    await store.dispatch('crm/updateMetaAds', payload);
    meta.value.access_token = '';
    useAlert('Configurações da Meta salvas');
  } catch {
    useAlert('Erro ao salvar configurações da Meta.');
  } finally {
    isMetaSaving.value = false;
  }
};

const testMeta = async () => {
  isMetaTesting.value = true;
  metaTestResult.value = null;
  try {
    const result = await store.dispatch('crm/testMetaAds');
    // 📣 item 345: um resultado por destino (site + cada caixa do WhatsApp)
    const results = result.results || [];
    metaTestResult.value = {
      success: !!result.success,
      results,
      message: results.length
        ? `${result.success ? 'Tudo chegou na Meta.' : 'Algum destino foi recusado — veja abaixo.'} Teste com o código ${result.test_event_code}: aparece só em "Testar eventos" da Meta e não conta nas campanhas.`
        : result.error || 'Falha no envio.',
    };
    store.dispatch('crm/fetchSettings'); // atualiza a lista de conjuntos do WhatsApp
  } catch {
    metaTestResult.value = { success: false, message: 'Erro inesperado ao testar.' };
  } finally {
    isMetaTesting.value = false;
  }
};

const saveGoogle = async () => {
  isGoogleSaving.value = true;
  googleTestResult.value = null;
  try {
    const payload = {};
    if (google.value.measurement_id.trim()) payload.measurement_id = google.value.measurement_id.trim();
    if (google.value.api_secret.trim()) payload.api_secret = google.value.api_secret.trim();
    if (google.value.client_id.trim()) payload.client_id = google.value.client_id.trim();
    if (google.value.developer_token.trim()) payload.developer_token = google.value.developer_token.trim();
    payload.customer_id = google.value.customer_id.trim();
    payload.ga4_property_id = google.value.ga4_property_id.trim();
    if (google.value.service_account_json.trim()) payload.service_account_json = google.value.service_account_json.trim();
    payload.search_console_site = google.value.search_console_site.trim();
    // 🎯 item 371
    payload.login_customer_id = google.value.login_customer_id.trim();
    if (google.value.oauth_client_id.trim()) payload.oauth_client_id = google.value.oauth_client_id.trim();
    if (google.value.oauth_client_secret.trim()) payload.oauth_client_secret = google.value.oauth_client_secret.trim();
    if (google.value.oauth_refresh_token.trim()) payload.oauth_refresh_token = google.value.oauth_refresh_token.trim();
    payload.ads_impersonate_email = google.value.ads_impersonate_email.trim();
    payload.conversions_via = google.value.conversions_via;
    await store.dispatch('crm/updateGoogleAds', payload);
    google.value.api_secret = '';
    google.value.developer_token = '';
    google.value.service_account_json = '';
    google.value.oauth_client_secret = '';
    google.value.oauth_refresh_token = '';
    useAlert('Configurações do Google salvas');
  } catch {
    useAlert('Erro ao salvar configurações do Google.');
  } finally {
    isGoogleSaving.value = false;
  }
};

const testGoogle = async () => {
  isGoogleTesting.value = true;
  googleTestResult.value = null;
  try {
    const result = await store.dispatch('crm/testGoogleAds');
    googleTestResult.value = result.success
      ? { success: true, message: 'Evento de teste enviado ao GA4!' }
      : { success: false, message: result.error || 'Falha no envio.' };
  } catch {
    googleTestResult.value = { success: false, message: 'Erro inesperado ao testar.' };
  } finally {
    isGoogleTesting.value = false;
  }
};

const testSearchConsole = async () => {
  isGscTesting.value = true;
  gscTestResult.value = null;
  try {
    const { data } = await CrmAPI.testSearchConsole();
    gscTestResult.value = data.success
      ? { success: true, message: `${data.message} A primeira foto dos 28 dias já foi pedida — em 1 minuto aparece em Resultados de tráfego → Otimizar.` }
      : { success: false, message: data.error || 'O Google não respondeu.' };
  } catch {
    gscTestResult.value = { success: false, message: 'Erro inesperado ao testar.' };
  } finally {
    isGscTesting.value = false;
  }
};

// 🎯 item 371: prepara o Google Ads (ações por etapa + variáveis) — salve antes
const prepareGoogleAds = async () => {
  isAdsPreparing.value = true;
  adsResult.value = null;
  try {
    const data = await store.dispatch('crm/prepareGoogleAds');
    const created = (data.created || []).length;
    adsResult.value = data.success
      ? { success: true, message: `Google Ads preparado: ${Object.keys(data.actions || {}).length} ações de conversão e ${Object.keys(data.variables || {}).length} variáveis.${created ? ` Criadas agora: ${data.created.join(', ')}.` : ' Já existiam — nada criado.'}` }
      : { success: false, message: data.error || 'O Google Ads não respondeu.' };
  } catch {
    adsResult.value = { success: false, message: 'Erro inesperado ao preparar.' };
  } finally {
    isAdsPreparing.value = false;
  }
};

const testGoogleAdsApi = async () => {
  isAdsTesting.value = true;
  adsResult.value = null;
  try {
    const data = await store.dispatch('crm/testGoogleAdsApi');
    adsResult.value = data.success
      ? { success: true, message: 'O Google Ads aceitou a conversão de teste (só validada, nada gravado). O envio direto está pronto.' }
      : { success: false, message: data.error || 'O Google Ads não respondeu.' };
  } catch {
    adsResult.value = { success: false, message: 'Erro inesperado ao testar.' };
  } finally {
    isAdsTesting.value = false;
  }
};

const adsActionsCount = computed(() => Object.keys(googleStatus.value.ads_actions || {}).length);

const copyGscEmail = async () => {
  try {
    await copyTextToClipboard(googleStatus.value.service_account_email || '');
    useAlert('E-mail copiado.');
  } catch {
    useAlert('Copie o e-mail à mão.');
  }
};

const isConfigured = computed(() => settings.value.n8n_api_key_configured && settings.value.n8n_base_url);
const workflowCount = computed(() => workflows.value?.length ?? 0);
const fetchedAt = computed(() => {
  const d = settings.value.n8n_workflows_fetched_at;
  if (!d) return null;
  return new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
});

const saveSettings = async () => {
  if (!n8nUrl.value.trim()) return;
  isSaving.value = true;
  testResult.value = null;
  try {
    const payload = { n8n_base_url: n8nUrl.value.trim() };
    if (n8nApiKey.value.trim()) payload.n8n_api_key = n8nApiKey.value.trim();
    await store.dispatch('crm/updateSettings', payload);
    n8nApiKey.value = '';
    useAlert('Configurações salvas');
  } catch {
    useAlert('Erro ao salvar configurações.');
  } finally {
    isSaving.value = false;
  }
};

const testConnection = async () => {
  isTesting.value = true;
  testResult.value = null;
  try {
    const result = await store.dispatch('crm/testN8n');
    testResult.value = result;
  } catch {
    testResult.value = { success: false, message: 'Erro inesperado ao testar conexão.' };
  } finally {
    isTesting.value = false;
  }
};

const fetchWorkflows = async () => {
  isFetching.value = true;
  try {
    const result = await store.dispatch('crm/fetchN8nWorkflows');
    if (result.success) {
      useAlert(`${result.count} workflow(s) importado(s) com sucesso`);
    } else {
      useAlert(result.message || 'Erro ao buscar workflows.');
    }
  } catch {
    useAlert('Erro ao buscar workflows.');
  } finally {
    isFetching.value = false;
  }
};
</script>

<template>
  <div
    :class="props.inline
      ? 'w-full flex justify-center'
      : 'fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4'"
    @click.self="!props.inline && emit('close')"
  >
    <div
      class="bg-n-solid-1 rounded-2xl flex flex-col w-full"
      :class="props.inline
        ? 'max-w-2xl border border-n-weak shadow-sm'
        : 'shadow-2xl max-w-lg max-h-[88vh]'"
    >

      <!-- Header -->
      <div class="flex items-center justify-between px-5 pt-5 pb-4 border-b border-n-weak flex-shrink-0">
        <div class="flex items-center gap-2.5">
          <span class="i-lucide-plug text-lg text-n-brand" />
          <h2 class="text-base font-semibold text-n-slate-12">Integrações</h2>
        </div>
        <button
          v-if="!props.inline"
          class="text-n-slate-10 hover:text-n-slate-12 i-lucide-x text-xl"
          @click="emit('close')"
        />
      </div>

      <!-- Body -->
      <div class="flex-1 overflow-y-auto p-5 space-y-6">

        <!-- n8n Section -->
        <div v-if="showSection('n8n')">
          <!-- Section header -->
          <div class="flex items-center gap-3 mb-4">
            <div class="w-9 h-9 rounded-xl bg-[#EA4B71]/10 flex items-center justify-center flex-shrink-0">
              <span class="i-lucide-workflow text-lg text-[#EA4B71]" />
            </div>
            <div>
              <h3 class="text-sm font-semibold text-n-slate-12">n8n</h3>
              <p class="text-xs text-n-slate-10">Conecte seu n8n para acionar workflows automaticamente</p>
            </div>
            <!-- Status badge -->
            <span
              class="ml-auto text-[11px] px-2 py-0.5 rounded-full font-medium flex-shrink-0"
              :class="isConfigured
                ? 'bg-green-500/15 text-green-600'
                : 'bg-n-alpha-2 text-n-slate-9'"
            >
              {{ isConfigured ? '✓ Conectado' : 'Não configurado' }}
            </span>
          </div>

          <!-- URL do n8n -->
          <div class="space-y-3">
            <div>
              <label class="text-xs font-medium text-n-slate-11 block mb-1.5">URL base do n8n</label>
              <input
                v-model="n8nUrl"
                class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-solid-2 text-n-slate-12 focus:outline-none focus:border-n-brand font-mono"
                placeholder="https://n8n.seudominio.com"
              />
            </div>

            <!-- API Key -->
            <div>
              <label class="text-xs font-medium text-n-slate-11 block mb-1.5">
                API Key
                <span v-if="isConfigured" class="text-green-600 font-normal ml-1">(já configurada)</span>
              </label>
              <input
                v-model="n8nApiKey"
                type="password"
                class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-solid-2 text-n-slate-12 focus:outline-none focus:border-n-brand font-mono"
                :placeholder="isConfigured ? 'Digite para substituir a key atual' : 'n8n_api_...'"
              />
              <p class="text-xs text-n-slate-9 mt-1">
                Encontre em: n8n → Settings → API → Create an API key
              </p>
            </div>

            <!-- Botões salvar + testar -->
            <div class="flex gap-2">
              <button
                class="flex-1 bg-n-brand text-white rounded-lg py-2 text-sm font-medium hover:bg-n-brand/90 disabled:opacity-50 transition-colors"
                :disabled="isSaving || !n8nUrl.trim()"
                @click="saveSettings"
              >
                {{ isSaving ? 'Salvando...' : 'Salvar' }}
              </button>
              <button
                class="px-4 py-2 border border-n-weak rounded-lg text-sm text-n-slate-11 hover:bg-n-alpha-1 disabled:opacity-50 transition-colors flex items-center gap-1.5"
                :disabled="isTesting || !isConfigured"
                @click="testConnection"
              >
                <span :class="isTesting ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-zap'" class="text-sm" />
                {{ isTesting ? 'Testando...' : 'Testar conexão' }}
              </button>
            </div>

            <!-- Test result -->
            <div
              v-if="testResult"
              class="flex items-start gap-2 px-3 py-2.5 rounded-lg text-sm"
              :class="testResult.success
                ? 'bg-green-500/10 text-green-700 border border-green-500/20'
                : 'bg-red-500/10 text-red-700 border border-red-500/20'"
            >
              <span
                class="text-base flex-shrink-0 mt-0.5"
                :class="testResult.success ? 'i-lucide-check-circle' : 'i-lucide-alert-circle'"
              />
              {{ testResult.message }}
            </div>
          </div>

          <!-- Divider -->
          <div class="border-t border-n-weak my-5" />

          <!-- Workflows section -->
          <div>
            <div class="flex items-center justify-between mb-3">
              <div>
                <h4 class="text-xs font-semibold text-n-slate-12">Workflows disponíveis</h4>
                <p v-if="fetchedAt" class="text-[11px] text-n-slate-9 mt-0.5">
                  Última importação: {{ fetchedAt }}
                </p>
              </div>
              <button
                class="flex items-center gap-1.5 px-3 py-1.5 border border-n-weak rounded-lg text-xs text-n-slate-11 hover:bg-n-alpha-1 disabled:opacity-50 transition-colors"
                :disabled="isFetching || !isConfigured"
                @click="fetchWorkflows"
              >
                <span :class="isFetching ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-refresh-cw'" class="text-sm" />
                {{ isFetching ? 'Buscando...' : 'Buscar workflows' }}
              </button>
            </div>

            <!-- Empty state -->
            <div
              v-if="workflowCount === 0"
              class="flex flex-col items-center justify-center py-8 bg-n-alpha-1 rounded-xl text-center"
            >
              <span class="i-lucide-workflow text-2xl text-n-slate-9 mb-2" />
              <p class="text-xs text-n-slate-10">
                {{ isConfigured
                  ? 'Clique em "Buscar workflows" para importar os workflows do seu n8n.'
                  : 'Configure a URL e a API Key para buscar workflows.' }}
              </p>
            </div>

            <!-- Workflow list -->
            <div v-else class="space-y-1.5">
              <div
                v-for="wf in workflows"
                :key="wf.id"
                class="flex items-center gap-3 px-3 py-2.5 bg-n-solid-2 rounded-lg border border-n-weak"
              >
                <span
                  class="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  :class="wf.active ? 'bg-green-500' : 'bg-n-slate-9'"
                />
                <span class="text-sm text-n-slate-12 flex-1 truncate">{{ wf.name }}</span>
                <span class="text-[10px] text-n-slate-9 font-mono flex-shrink-0">{{ wf.id }}</span>
                <span
                  class="text-[10px] px-1.5 py-0.5 rounded font-medium flex-shrink-0"
                  :class="wf.active ? 'bg-green-500/15 text-green-600' : 'bg-n-alpha-2 text-n-slate-9'"
                >
                  {{ wf.active ? 'Ativo' : 'Off' }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- ══ Meta (Facebook / Instagram Ads) ══ -->
        <div v-if="showSection('meta')" :class="props.only ? '' : 'border-t border-n-weak pt-5'">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-9 h-9 rounded-xl bg-[#1877F2]/10 flex items-center justify-center flex-shrink-0">
              <span class="i-lucide-facebook text-lg text-[#1877F2]" />
            </div>
            <div class="min-w-0">
              <h3 class="text-sm font-semibold text-n-slate-12">Meta — Facebook e Instagram Ads</h3>
              <p class="text-xs text-n-slate-10">Envia conversões (CAPI) e recebe dados das campanhas (Insights)</p>
            </div>
            <div class="ml-auto flex flex-col items-end gap-1 flex-shrink-0">
              <span
                class="text-[10px] px-2 py-0.5 rounded-full font-medium"
                :class="metaStatus.configured ? 'bg-green-500/15 text-green-600' : 'bg-n-alpha-2 text-n-slate-9'"
              >
                {{ metaStatus.configured ? '✓ Envio (CAPI)' : 'Envio: pendente' }}
              </span>
              <span
                class="text-[10px] px-2 py-0.5 rounded-full font-medium"
                :class="metaStatus.insights_configured ? 'bg-green-500/15 text-green-600' : 'bg-n-alpha-2 text-n-slate-9'"
              >
                {{ metaStatus.insights_configured ? '✓ Recebimento (Insights)' : 'Recebimento: pendente' }}
              </span>
            </div>
          </div>

          <!-- Requisitos -->
          <button
            class="w-full flex items-center gap-2 text-xs text-n-brand mb-3"
            @click="showMetaRequirements = !showMetaRequirements"
          >
            <span :class="showMetaRequirements ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'" />
            O que é necessário para integrar com a Meta
          </button>
          <div v-if="showMetaRequirements" class="bg-n-alpha-1 rounded-xl p-3.5 mb-4 text-xs text-n-slate-11 space-y-2">
            <p><b>1. Business Manager</b> da clínica com acesso ao Pixel e à conta de anúncios.</p>
            <p><b>2. ID do Pixel</b> — Gerenciador de Eventos → Fontes de dados → seu Pixel (número de ~15 dígitos).</p>
            <p><b>3. Token de acesso do sistema</b> — Configurações do negócio → Usuários → Usuários do sistema → gerar token com permissões <code class="bg-n-alpha-2 px-1 rounded">ads_read</code> e <code class="bg-n-alpha-2 px-1 rounded">ads_management</code>.</p>
            <p><b>4. ID da conta de anúncios</b> (para RECEBER insights) — formato <code class="bg-n-alpha-2 px-1 rounded">act_1234567890</code>.</p>
            <p><b>5. Código de evento de teste</b> (opcional) — Gerenciador de Eventos → Testar eventos.</p>
            <p class="text-n-slate-9">Com isso o sistema ENVIA eventos (lead, agendamento, cirurgia — via automações do CRM) e RECEBE investimento/resultados das campanhas para o Funil de Tráfego e Dashboard.</p>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="text-xs font-medium text-n-slate-11 block mb-1.5">ID do Pixel</label>
              <input
                v-model="meta.pixel_id"
                class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-solid-2 text-n-slate-12 font-mono"
                placeholder="123456789012345"
              />
            </div>
            <div>
              <label class="text-xs font-medium text-n-slate-11 block mb-1.5">
                Token de acesso
                <span v-if="metaStatus.access_token_set" class="text-green-600 font-normal">(já configurado)</span>
              </label>
              <input
                v-model="meta.access_token"
                type="password"
                class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-solid-2 text-n-slate-12 font-mono"
                :placeholder="metaStatus.access_token_set ? 'Digite para substituir' : 'EAAxxxx...'"
              />
            </div>
            <div>
              <label class="text-xs font-medium text-n-slate-11 block mb-1.5">ID da conta de anúncios</label>
              <input
                v-model="meta.ad_account_id"
                class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-solid-2 text-n-slate-12 font-mono"
                placeholder="act_1234567890"
              />
            </div>
            <div>
              <label class="text-xs font-medium text-n-slate-11 block mb-1.5">Código de teste <span class="text-n-slate-9">(opcional)</span></label>
              <input
                v-model="meta.test_event_code"
                class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-solid-2 text-n-slate-12 font-mono"
                placeholder="TEST12345"
              />
            </div>
          </div>

          <div class="flex gap-2 mt-3">
            <button
              class="flex-1 bg-n-brand text-white rounded-lg py-2 text-sm font-medium hover:bg-n-brand/90 disabled:opacity-50 transition-colors"
              :disabled="isMetaSaving"
              @click="saveMeta"
            >
              {{ isMetaSaving ? 'Salvando...' : 'Salvar' }}
            </button>
            <button
              class="px-4 py-2 border border-n-weak rounded-lg text-sm text-n-slate-11 hover:bg-n-alpha-1 disabled:opacity-50 transition-colors flex items-center gap-1.5"
              :disabled="isMetaTesting || !metaStatus.configured"
              @click="testMeta"
            >
              <span :class="isMetaTesting ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-zap'" class="text-sm" />
              {{ isMetaTesting ? 'Testando...' : 'Testar envio' }}
            </button>
          </div>

          <div
            v-if="metaTestResult"
            class="mt-3 flex items-start gap-2 px-3 py-2.5 rounded-lg text-sm"
            :class="metaTestResult.success
              ? 'bg-green-500/10 text-green-700 border border-green-500/20'
              : 'bg-red-500/10 text-red-700 border border-red-500/20'"
          >
            <span
              class="text-base flex-shrink-0 mt-0.5"
              :class="metaTestResult.success ? 'i-lucide-check-circle' : 'i-lucide-alert-circle'"
            />
            {{ metaTestResult.message }}
          </div>
          <ul v-if="metaTestResult?.results?.length" class="mt-2 space-y-1 text-xs">
            <li
              v-for="r in metaTestResult.results"
              :key="r.target"
              class="flex items-start gap-1.5 px-3 py-1.5 rounded-lg bg-n-alpha-1 text-n-slate-11"
            >
              <span class="flex-shrink-0">{{ r.skipped ? '⚪' : r.ok ? '✅' : '❌' }}</span>
              <span class="min-w-0">
                <b class="text-n-slate-12">{{ r.target }}</b>
                <template v-if="r.skipped"> — {{ r.note }}</template>
                <template v-else-if="r.ok">
                  — chegou na Meta<span v-if="r.token"> (chave {{ r.token === 'caixa' ? 'da caixa' : 'do sistema' }})</span>
                  <span v-if="r.note" class="text-n-slate-10"> · {{ r.note }}</span>
                </template>
                <template v-else> — {{ r.error || 'a Meta recusou' }}</template>
              </span>
            </li>
          </ul>
          <p v-if="metaStatus.whatsapp_datasets?.length" class="mt-2 text-xs text-n-slate-10">
            <b class="text-n-slate-11">Conjuntos do WhatsApp:</b>
            <span v-for="(d, i) in metaStatus.whatsapp_datasets" :key="d.waba_id">
              {{ i ? ' · ' : ' ' }}Caixa {{ d.inbox_id }} {{ d.inbox_name }} →
              <span class="font-mono">{{ d.dataset_id || 'ainda não criado (o Testar envio cria)' }}</span>
            </span>
          </p>

          <!-- Atribuição de anúncio (CTWA) — automática -->
          <div class="mt-3 bg-n-alpha-1 rounded-xl p-3.5 text-xs text-n-slate-11 space-y-1.5">
            <p class="flex items-center gap-1.5 font-medium text-n-slate-12">
              <span class="i-lucide-megaphone text-blue-500" />
              Atribuição de anúncio (click-to-WhatsApp) — automática
            </p>
            <p>
              Quando o paciente chega clicando em um anúncio, a Meta manda os dados
              do anúncio junto com a primeira mensagem. O sistema grava isso no
              contato ("Veio de anúncio") e usa nas conversões enviadas (ctwa_clid) —
              não precisa configurar nada.
            </p>
            <p>
              O ranking completo (qual anúncio gerou cirurgia, CPL, CAC, ROAS) fica
              em <b>Relatórios → Anúncios (Meta)</b>, onde também dá para processar
              o histórico de mensagens antigas.
            </p>
          </div>
        </div>

        <!-- ══ Google (GA4 + Google Ads) ══ -->
        <div v-if="showSection('google')" :class="props.only ? '' : 'border-t border-n-weak pt-5'">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-9 h-9 rounded-xl bg-[#4285F4]/10 flex items-center justify-center flex-shrink-0">
              <span class="i-lucide-chrome text-lg text-[#4285F4]" />
            </div>
            <div class="min-w-0">
              <h3 class="text-sm font-semibold text-n-slate-12">Google — GA4 e Google Ads</h3>
              <p class="text-xs text-n-slate-10">Envia conversões (GA4 ou direto no Google Ads pelo gclid) e lê o investimento</p>
            </div>
            <div class="ml-auto flex flex-col items-end gap-1 flex-shrink-0">
              <span
                class="text-[10px] px-2 py-0.5 rounded-full font-medium"
                :class="googleStatus.configured ? 'bg-green-500/15 text-green-600' : 'bg-n-alpha-2 text-n-slate-9'"
              >
                {{ googleStatus.configured ? '✓ Envio (GA4)' : 'Envio: pendente' }}
              </span>
              <span
                class="text-[10px] px-2 py-0.5 rounded-full font-medium"
                :class="googleStatus.insights_configured ? 'bg-green-500/15 text-green-600' : 'bg-amber-500/15 text-amber-600'"
              >
                {{ googleStatus.insights_configured ? '✓ Token do Ads salvo' : 'Recebimento: aguarda token' }}
              </span>
            </div>
          </div>

          <!-- Requisitos -->
          <button
            class="w-full flex items-center gap-2 text-xs text-n-brand mb-3"
            @click="showGoogleRequirements = !showGoogleRequirements"
          >
            <span :class="showGoogleRequirements ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'" />
            O que é necessário para integrar com o Google
          </button>
          <div v-if="showGoogleRequirements" class="bg-n-alpha-1 rounded-xl p-3.5 mb-4 text-xs text-n-slate-11 space-y-2">
            <p><b>Para ENVIAR conversões (GA4):</b></p>
            <p><b>1. Measurement ID</b> — GA4 → Administrador → Fluxos de dados → seu site (formato <code class="bg-n-alpha-2 px-1 rounded">G-XXXXXXX</code>).</p>
            <p><b>2. API Secret</b> — no mesmo fluxo de dados → Measurement Protocol → Criar chave secreta.</p>
            <p class="pt-1"><b>Para RECEBER dados do Google Ads (investimento/campanhas):</b></p>
            <p><b>3. Developer Token</b> — Google Ads → Ferramentas → Central de API. ⚠ Aprovação do Google pode levar semanas; a tela já está pronta para quando chegar.</p>
            <p><b>4. Customer ID</b> — número da conta Google Ads (formato <code class="bg-n-alpha-2 px-1 rounded">123-456-7890</code>).</p>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="text-xs font-medium text-n-slate-11 block mb-1.5">Measurement ID (GA4)</label>
              <input
                v-model="google.measurement_id"
                class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-solid-2 text-n-slate-12 font-mono"
                placeholder="G-XXXXXXXXXX"
              />
            </div>
            <div>
              <label class="text-xs font-medium text-n-slate-11 block mb-1.5">
                API Secret
                <span v-if="googleStatus.api_secret_set" class="text-green-600 font-normal">(já configurada)</span>
              </label>
              <input
                v-model="google.api_secret"
                type="password"
                class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-solid-2 text-n-slate-12 font-mono"
                :placeholder="googleStatus.api_secret_set ? 'Digite para substituir' : 'xxxxxxx'"
              />
            </div>
            <div>
              <label class="text-xs font-medium text-n-slate-11 block mb-1.5">
                Developer Token <span class="text-n-slate-9">(Google Ads)</span>
                <span v-if="googleStatus.developer_token_set" class="text-green-600 font-normal">(já configurado)</span>
              </label>
              <input
                v-model="google.developer_token"
                type="password"
                class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-solid-2 text-n-slate-12 font-mono"
                :placeholder="googleStatus.developer_token_set ? 'Digite para substituir' : 'Aguardando aprovação do Google'"
              />
            </div>
            <div>
              <label class="text-xs font-medium text-n-slate-11 block mb-1.5">Customer ID <span class="text-n-slate-9">(Google Ads)</span></label>
              <input
                v-model="google.customer_id"
                class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-solid-2 text-n-slate-12 font-mono"
                placeholder="123-456-7890"
              />
            </div>
          </div>

          <!-- 🎯 item 371 (27/10): conversões DIRETO no Google Ads pelo código do
               clique (gclid/gbraid/wbraid) guardado no Protocolo, com valor e
               procedimento; sem clique, conversão aprimorada (telefone/e-mail). -->
          <div class="mt-3 rounded-xl border border-n-weak bg-n-alpha-1 p-3 space-y-3">
            <p class="text-xs font-medium text-n-slate-12">
              🎯 Conversões direto no Google Ads (gclid)
              <span v-if="googleStatus.conversions_via === 'ads_api' && googleStatus.ads_api_configured && adsActionsCount" class="text-green-600 font-normal">· ligado ✓ ({{ adsActionsCount }} ações)</span>
              <span v-else-if="googleStatus.ads_api_configured" class="text-amber-600 font-normal">· acesso ok — falta preparar e ligar</span>
              <span v-else class="text-amber-600 font-normal">· desligado — as conversões seguem pelo GA4</span>
            </p>
            <p class="text-[11px] text-n-slate-10 leading-relaxed">
              Hoje a conversão vai ao GA4 e o Google Ads importa. Ligado, o sistema devolve cada etapa (agendou, compareceu,
              fechou cirurgia) <b>direto no Google Ads</b>, amarrada ao clique do anúncio, com <b>valor</b> e <b>procedimento</b>.
              Sem clique guardado, vai a conversão aprimorada (telefone e e-mail embaralhados).
            </p>
            <button class="flex items-center gap-2 text-xs text-n-brand" @click="showAdsHelp = !showAdsHelp">
              <span :class="showAdsHelp ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'" />
              Como ligar (uma vez)
            </button>
            <div v-if="showAdsHelp" class="text-[11px] text-n-slate-10 leading-relaxed space-y-0.5">
              <p><b>1.</b> Developer Token e Customer ID acima (Google Ads → Ferramentas → Central de API). Conta gerenciada por uma MCC: informe o ID da MCC em "Conta de login".</p>
              <p><b>2.</b> Acesso — um dos dois: <b>OAuth</b> (console.cloud.google.com → Credenciais → ID do cliente OAuth → gere o refresh token com a conta que acessa o Ads, escopo adwords) ou <b>conta de serviço</b> (a mesma do GA4, com delegação em todo o domínio no Workspace, representando um e-mail que acessa o Ads).</p>
              <p><b>3.</b> Salvar → <b>Preparar no Google Ads</b> (cria as 5 ações "CEVICO · …" e as variáveis procedimento/pagina) → <b>Testar envio</b> → escolha "Direto no Google Ads" e salve.</p>
              <p><b>4.</b> No Google Ads: deixe as ações "CEVICO · …" como <b>principais</b> e as importadas do GA4 como secundárias (senão conta duas vezes). Para a conversão aprimorada, ative "conversões aprimoradas para leads" em cada ação.</p>
            </div>
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="text-xs font-medium text-n-slate-11 block mb-1.5">Conta de login (MCC) <span class="text-n-slate-9 font-normal">(opcional)</span></label>
                <input v-model="google.login_customer_id" class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-solid-2 text-n-slate-12 font-mono" placeholder="123-456-7890" />
              </div>
              <div>
                <label class="text-xs font-medium text-n-slate-11 block mb-1.5">Envio das conversões</label>
                <select v-model="google.conversions_via" class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-solid-2 text-n-slate-12">
                  <option value="ga4">Pelo GA4 (como hoje)</option>
                  <option value="ads_api" :disabled="!googleStatus.ads_api_configured || !adsActionsCount">Direto no Google Ads (gclid)</option>
                </select>
              </div>
              <div>
                <label class="text-xs font-medium text-n-slate-11 block mb-1.5">OAuth — ID do cliente</label>
                <input v-model="google.oauth_client_id" class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-solid-2 text-n-slate-12 font-mono" placeholder="xxxx.apps.googleusercontent.com" />
              </div>
              <div>
                <label class="text-xs font-medium text-n-slate-11 block mb-1.5">
                  OAuth — chave secreta
                  <span v-if="googleStatus.oauth_secret_set" class="text-green-600 font-normal">(já configurada)</span>
                </label>
                <input v-model="google.oauth_client_secret" type="password" class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-solid-2 text-n-slate-12 font-mono" :placeholder="googleStatus.oauth_secret_set ? 'Digite para substituir' : 'GOCSPX-…'" />
              </div>
              <div>
                <label class="text-xs font-medium text-n-slate-11 block mb-1.5">
                  OAuth — refresh token
                  <span v-if="googleStatus.oauth_refresh_token_set" class="text-green-600 font-normal">(já configurado)</span>
                </label>
                <input v-model="google.oauth_refresh_token" type="password" class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-solid-2 text-n-slate-12 font-mono" :placeholder="googleStatus.oauth_refresh_token_set ? 'Digite para substituir' : '1//0…'" />
              </div>
              <div>
                <label class="text-xs font-medium text-n-slate-11 block mb-1.5">ou: e-mail que a conta de serviço representa</label>
                <input v-model="google.ads_impersonate_email" class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-solid-2 text-n-slate-12 font-mono" placeholder="ads@cevico.com.br" />
              </div>
            </div>
            <div class="flex items-center gap-2 flex-wrap">
              <button
                class="px-4 py-2 border border-n-weak rounded-lg text-sm text-n-slate-11 hover:bg-n-alpha-1 disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
                :disabled="isAdsPreparing || !googleStatus.ads_api_configured"
                :title="googleStatus.ads_api_configured ? 'Cria (uma vez) as ações de conversão e as variáveis na conta do Google Ads — salve antes' : 'Salve o developer token, o Customer ID e o acesso primeiro'"
                @click="prepareGoogleAds"
              >
                <span :class="isAdsPreparing ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-wand-2'" class="text-sm" />
                {{ isAdsPreparing ? 'Preparando...' : 'Preparar no Google Ads' }}
              </button>
              <button
                class="px-4 py-2 border border-n-weak rounded-lg text-sm text-n-slate-11 hover:bg-n-alpha-1 disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
                :disabled="isAdsTesting || !googleStatus.ads_api_configured || !adsActionsCount"
                title="Manda uma conversão de teste só para o Google validar (nada é gravado)"
                @click="testGoogleAdsApi"
              >
                <span :class="isAdsTesting ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-send'" class="text-sm" />
                {{ isAdsTesting ? 'Testando...' : 'Testar envio' }}
              </button>
              <span v-if="googleStatus.ads_prepared_at" class="text-[11px] text-n-slate-10">preparado em {{ new Date(googleStatus.ads_prepared_at).toLocaleString('pt-BR') }}</span>
            </div>
            <div
              v-if="adsResult"
              class="flex items-start gap-2 px-3 py-2.5 rounded-lg text-xs"
              :class="adsResult.success ? 'bg-green-500/10 text-green-700 border border-green-500/20' : 'bg-red-500/10 text-red-700 border border-red-500/20'"
            >
              <span class="text-sm flex-shrink-0 mt-0.5" :class="adsResult.success ? 'i-lucide-check-circle' : 'i-lucide-alert-circle'" />
              {{ adsResult.message }}
            </div>
            <p v-else-if="googleStatus.ads_last_error" class="text-[11px] text-red-600">
              Última recusa do Google Ads ({{ new Date(googleStatus.ads_last_error.at).toLocaleString('pt-BR') }}, {{ googleStatus.ads_last_error.event }}): {{ googleStatus.ads_last_error.message }}
            </p>
          </div>

          <!-- 📖 LEITURA automática via GA4 Data API (item 155): cliques, custo
               e CPC por palavra-chave + investimento da caixa GOOGLE — sem
               precisar do developer token. Os avisos das telas apontam pra cá. -->
          <div class="mt-3 rounded-xl border border-n-weak bg-n-alpha-1 p-3 space-y-3">
            <p class="text-xs font-medium text-n-slate-12">
              📖 Leitura automática (GA4)
              <span v-if="googleStatus.cost_configured" class="text-green-600 font-normal">· conectada ✓</span>
              <span v-else class="text-amber-600 font-normal">· falta configurar — é o que acende cliques, custo por palavra-chave e o investimento da caixa GOOGLE</span>
            </p>
            <div class="text-[11px] text-n-slate-10 leading-relaxed space-y-0.5">
              <p><b>1.</b> console.cloud.google.com → ative a API <b>"Google Analytics Data API"</b> → IAM → <b>Contas de serviço</b> → criar (sem papel) → Chaves → <b>Adicionar chave JSON</b> (baixa o arquivo).</p>
              <p><b>2.</b> No GA4: Administrador → <b>Gerenciamento de acesso à propriedade</b> → adicione o e-mail da conta de serviço como <b>Leitor</b>.</p>
              <p><b>3.</b> Propriedade: GA4 → Administrador → Configurações da propriedade → número (ex.: 123456789).</p>
            </div>
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="text-xs font-medium text-n-slate-11 block mb-1.5">Propriedade do GA4</label>
                <input
                  v-model="google.ga4_property_id"
                  class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-solid-2 text-n-slate-12 font-mono"
                  placeholder="123456789"
                />
              </div>
              <div>
                <label class="text-xs font-medium text-n-slate-11 block mb-1.5">
                  JSON da conta de serviço
                  <span v-if="googleStatus.service_account_set" class="text-green-600 font-normal">(já configurado)</span>
                </label>
                <textarea
                  v-model="google.service_account_json"
                  rows="2"
                  class="w-full border border-n-weak rounded-lg px-3 py-2 text-xs bg-n-solid-2 text-n-slate-12 font-mono"
                  :placeholder="googleStatus.service_account_set ? 'Cole para substituir' : 'Cole o conteúdo do arquivo .json inteiro'"
                />
              </div>
            </div>
          </div>

          <!-- 🔎 SEARCH CONSOLE (16/10): termos, aparições, cliques e posição das
               páginas no Google → Resultados de tráfego → Otimizar. Usa a MESMA
               conta de serviço do bloco acima; o e-mail dela entra como usuário
               da propriedade no Search Console. -->
          <div class="mt-3 rounded-xl border border-n-weak bg-n-alpha-1 p-3 space-y-3">
            <p class="text-xs font-medium text-n-slate-12">
              🔎 Search Console (posição no Google)
              <span v-if="googleStatus.search_console_fetched_at && !googleStatus.search_console_error" class="text-green-600 font-normal">· conectado ✓</span>
              <span v-else-if="googleStatus.search_console_error" class="text-red-600 font-normal">· {{ googleStatus.search_console_error }}</span>
              <span v-else class="text-amber-600 font-normal">· falta ligar — é o que mostra termos buscados, cliques e posição de cada página</span>
            </p>
            <div class="text-[11px] text-n-slate-10 leading-relaxed space-y-0.5">
              <p><b>1.</b> Precisa do JSON da conta de serviço colado acima (se já colou para o GA4, é o mesmo).</p>
              <p><b>2.</b> console.cloud.google.com → no mesmo projeto → APIs e serviços → Biblioteca → ative <b>"Google Search Console API"</b>.</p>
              <p><b>3.</b> search.google.com/search-console → propriedade <b>cevico.com.br</b> → Configurações → <b>Usuários e permissões</b> → Adicionar usuário → cole o e-mail abaixo → permissão <b>Restrito</b> (só leitura).</p>
              <p><b>4.</b> Salvar e <b>Testar Search Console</b>. Daí em diante a foto chega sozinha toda manhã.</p>
            </div>
            <div v-if="googleStatus.service_account_email" class="flex items-center gap-2 flex-wrap">
              <span class="text-[11px] text-n-slate-11">E-mail da conta de serviço:</span>
              <code class="text-[11px] px-2 py-1 rounded bg-n-solid-2 border border-n-weak text-n-slate-12 break-all">{{ googleStatus.service_account_email }}</code>
              <button class="text-[11px] text-n-brand hover:underline" @click="copyGscEmail">copiar</button>
            </div>
            <div class="grid grid-cols-2 gap-3 items-end">
              <div>
                <label class="text-xs font-medium text-n-slate-11 block mb-1.5">Propriedade do Search Console</label>
                <input
                  v-model="google.search_console_site"
                  class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-solid-2 text-n-slate-12 font-mono"
                  placeholder="sc-domain:cevico.com.br"
                />
              </div>
              <button
                class="px-4 py-2 border border-n-weak rounded-lg text-sm text-n-slate-11 hover:bg-n-alpha-1 disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
                :disabled="isGscTesting || !googleStatus.service_account_set"
                :title="googleStatus.service_account_set ? 'Faz uma pergunta pequena ao Google (salve antes se mudou algo)' : 'Cole e salve o JSON da conta de serviço primeiro'"
                @click="testSearchConsole"
              >
                <span :class="isGscTesting ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-search'" class="text-sm" />
                {{ isGscTesting ? 'Testando...' : 'Testar Search Console' }}
              </button>
            </div>
            <div
              v-if="gscTestResult"
              class="flex items-start gap-2 px-3 py-2.5 rounded-lg text-xs"
              :class="gscTestResult.success
                ? 'bg-green-500/10 text-green-700 border border-green-500/20'
                : 'bg-red-500/10 text-red-700 border border-red-500/20'"
            >
              <span
                class="text-sm flex-shrink-0 mt-0.5"
                :class="gscTestResult.success ? 'i-lucide-check-circle' : 'i-lucide-alert-circle'"
              />
              {{ gscTestResult.message }}
            </div>
          </div>

          <div class="flex gap-2 mt-3">
            <button
              class="flex-1 bg-n-brand text-white rounded-lg py-2 text-sm font-medium hover:bg-n-brand/90 disabled:opacity-50 transition-colors"
              :disabled="isGoogleSaving"
              @click="saveGoogle"
            >
              {{ isGoogleSaving ? 'Salvando...' : 'Salvar' }}
            </button>
            <button
              class="px-4 py-2 border border-n-weak rounded-lg text-sm text-n-slate-11 hover:bg-n-alpha-1 disabled:opacity-50 transition-colors flex items-center gap-1.5"
              :disabled="isGoogleTesting || !googleStatus.configured"
              @click="testGoogle"
            >
              <span :class="isGoogleTesting ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-zap'" class="text-sm" />
              {{ isGoogleTesting ? 'Testando...' : 'Testar envio' }}
            </button>
          </div>

          <div
            v-if="googleTestResult"
            class="mt-3 flex items-start gap-2 px-3 py-2.5 rounded-lg text-sm"
            :class="googleTestResult.success
              ? 'bg-green-500/10 text-green-700 border border-green-500/20'
              : 'bg-red-500/10 text-red-700 border border-red-500/20'"
          >
            <span
              class="text-base flex-shrink-0 mt-0.5"
              :class="googleTestResult.success ? 'i-lucide-check-circle' : 'i-lucide-alert-circle'"
            />
            {{ googleTestResult.message }}
          </div>
        </div>

        <!-- ══ Claude (IA nativa do sistema) ══ -->
        <div v-if="showSection('ai')" :class="props.only ? '' : 'border-t border-n-weak pt-5'">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-9 h-9 rounded-xl bg-[#D97706]/10 flex items-center justify-center flex-shrink-0">
              <span class="i-lucide-sparkles text-lg text-[#D97706]" />
            </div>
            <div class="min-w-0">
              <h3 class="text-sm font-semibold text-n-slate-12">Claude — IA nativa do sistema</h3>
              <p class="text-xs text-n-slate-10">Análise de conversas (interesse do paciente) e insights de marketing dos formulários</p>
            </div>
            <span
              class="ml-auto text-[10px] px-2 py-0.5 rounded-full font-medium flex-shrink-0"
              :class="aiStatus.configured ? 'bg-green-500/15 text-green-600' : 'bg-n-alpha-2 text-n-slate-9'"
            >
              {{ aiStatus.configured ? '✓ Conectada' : 'Pendente' }}
            </span>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="text-xs font-medium text-n-slate-11 block mb-1.5">
                Chave da API (Anthropic)
                <span v-if="aiStatus.api_key_set" class="text-green-600 font-normal">(já configurada)</span>
              </label>
              <input
                v-model="ai.api_key"
                type="password"
                class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-solid-2 text-n-slate-12 font-mono"
                :placeholder="aiStatus.api_key_set ? 'Digite para substituir' : 'sk-ant-...'"
              />
            </div>
            <div>
              <label class="text-xs font-medium text-n-slate-11 block mb-1.5">Modelo padrão</label>
              <select
                v-model="ai.model"
                class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-solid-2 text-n-slate-12"
              >
                <option v-for="m in AI_MODELS" :key="m.value" :value="m.value">{{ m.label }}</option>
              </select>
            </div>
            <div class="col-span-2">
              <label class="text-xs font-medium text-n-slate-11 block mb-1.5">
                Esforço padrão
                <span class="text-n-slate-9 font-normal">(quanto a IA pensa antes de responder)</span>
              </label>
              <select
                v-model="ai.effort"
                class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-solid-2 text-n-slate-12"
              >
                <option v-for="e in AI_EFFORTS" :key="e.value" :value="e.value">{{ e.label }}</option>
              </select>
            </div>
          </div>

          <p class="text-[11px] text-n-slate-9 mt-2">
            Crie a chave em console.anthropic.com → API Keys. Modelo e esforço aqui são o
            PADRÃO — cada agente pode usar valores próprios em Automações → Agentes de IA.
            O Haiku ignora o campo de esforço.
          </p>

          <div class="flex gap-2 mt-3">
            <button
              class="flex-1 bg-n-brand text-white rounded-lg py-2 text-sm font-medium hover:bg-n-brand/90 disabled:opacity-50 transition-colors"
              :disabled="isAiSaving"
              @click="saveAi"
            >
              {{ isAiSaving ? 'Salvando...' : 'Salvar' }}
            </button>
            <button
              class="px-4 py-2 border border-n-weak rounded-lg text-sm text-n-slate-11 hover:bg-n-alpha-1 disabled:opacity-50 transition-colors flex items-center gap-1.5"
              :disabled="isAiTesting || !aiStatus.configured"
              @click="testAi"
            >
              <span :class="isAiTesting ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-zap'" class="text-sm" />
              {{ isAiTesting ? 'Testando...' : 'Testar conexão' }}
            </button>
          </div>

          <div
            v-if="aiTestResult"
            class="mt-3 flex items-start gap-2 px-3 py-2.5 rounded-lg text-sm"
            :class="aiTestResult.success
              ? 'bg-green-500/10 text-green-700 border border-green-500/20'
              : 'bg-red-500/10 text-red-700 border border-red-500/20'"
          >
            <span
              class="text-base flex-shrink-0 mt-0.5"
              :class="aiTestResult.success ? 'i-lucide-check-circle' : 'i-lucide-alert-circle'"
            />
            {{ aiTestResult.message }}
          </div>
        </div>

        <!-- ══ Google Sheets (planilha de cirurgias) ══ -->
        <!-- ══ Gemini (Google) — imagens por IA nas Páginas ══ -->
        <div v-if="showSection('gemini')" :class="props.only ? '' : 'border-t border-n-weak pt-5'">
          <div class="flex items-center gap-2 mb-1">
            <span class="w-7 h-7 rounded-lg flex items-center justify-center" style="background: linear-gradient(135deg, #1A73E8, #9B72CB, #D96570)">
              <span class="i-lucide-image-plus text-white text-sm" />
            </span>
            <p class="text-sm font-semibold text-n-slate-12">Gemini — IA do Google</p>
            <span
              class="text-[10px] px-2 py-0.5 rounded-full font-medium"
              :class="aiStatus.gemini_key_set ? 'bg-green-500/15 text-green-600' : 'bg-n-alpha-2 text-n-slate-10'"
            >
              ● {{ aiStatus.gemini_key_set ? 'Conectado' : 'Não configurado' }}
            </span>
          </div>
          <p class="text-xs text-n-slate-10 mb-3">
            Reservado para GERAR IMAGENS nas Páginas (heros, ilustrações de procedimentos).
            A chave sai do <a href="https://aistudio.google.com/apikey" target="_blank" rel="noopener" class="text-n-brand hover:underline">Google AI Studio</a> — gratuita para começar.
          </p>
          <div>
            <label class="text-xs font-medium text-n-slate-11 block mb-1.5">
              Chave da API (Google AI Studio)
              <span v-if="aiStatus.gemini_key_set" class="text-green-600 font-normal">(já configurada)</span>
            </label>
            <input
              v-model="gemini.api_key"
              type="password"
              class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-solid-2 text-n-slate-12 font-mono"
              :placeholder="aiStatus.gemini_key_set ? 'Digite para substituir' : 'AIza...'"
            />
          </div>
          <div class="flex items-center gap-2 mt-3 flex-wrap">
            <button
              class="h-9 px-4 rounded-lg bg-n-brand text-white text-sm font-medium hover:bg-n-brand/90 disabled:opacity-50"
              :disabled="isGeminiSaving"
              @click="saveGemini"
            >
              {{ isGeminiSaving ? 'Salvando…' : 'Salvar chave' }}
            </button>
            <button
              class="h-9 px-4 rounded-lg border border-n-weak text-sm text-n-slate-11 hover:bg-n-alpha-1 disabled:opacity-50"
              :disabled="isGeminiTesting || !aiStatus.gemini_key_set"
              @click="testGeminiConnection"
            >
              {{ isGeminiTesting ? 'Testando…' : 'Testar conexão' }}
            </button>
            <span
              v-if="geminiTestResult"
              class="text-xs font-medium"
              :class="geminiTestResult.success ? 'text-green-600' : 'text-red-500'"
            >
              {{ geminiTestResult.message }}
            </span>
          </div>
          <p class="text-[11px] text-n-slate-9 mt-2">
            O botão "Gerar imagem" nas seções das Páginas entra na próxima rodada — a conexão já fica pronta.
          </p>
        </div>

        <div v-if="showSection('sheets')" :class="props.only ? '' : 'border-t border-n-weak pt-5'">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-9 h-9 rounded-xl bg-[#0F9D58]/10 flex items-center justify-center flex-shrink-0">
              <span class="i-lucide-sheet text-lg text-[#0F9D58]" />
            </div>
            <div class="min-w-0">
              <h3 class="text-sm font-semibold text-n-slate-12">Google Sheets — planilha de cirurgias</h3>
              <p class="text-xs text-n-slate-10">Os dados da planilha entram no Dashboard CEVICO (bloco Cirurgias)</p>
            </div>
            <span
              class="ml-auto text-[10px] px-2 py-0.5 rounded-full font-medium flex-shrink-0"
              :class="sheetsStatus.configured ? 'bg-green-500/15 text-green-600' : 'bg-n-alpha-2 text-n-slate-9'"
            >
              {{ sheetsStatus.configured ? '✓ Conectada' : 'Pendente' }}
            </span>
          </div>

          <div class="bg-n-alpha-1 rounded-xl p-3.5 mb-4 text-xs text-n-slate-11 space-y-1.5">
            <p class="font-medium text-n-slate-12">Como preparar a planilha (2 passos):</p>
            <p><b>1.</b> No Google Sheets: Compartilhar → Acesso geral → <b>"Qualquer pessoa com o link"</b> (Leitor) → copiar o link.</p>
            <p><b>2.</b> A primeira linha precisa ter os títulos das colunas. O sistema reconhece:
              <b>Data</b>, <b>Nome</b> (ou Paciente), <b>Telefone</b>, <b>Procedimento</b> (ou Cirurgia), <b>Valor</b> e <b>Unidade</b>.</p>
            <p class="text-n-slate-9">Só Data e Nome são obrigatórias — as demais enriquecem o Dashboard. A planilha é relida automaticamente a cada 1 hora.</p>
          </div>

          <div>
            <label class="text-xs font-medium text-n-slate-11 block mb-1.5">Link da planilha</label>
            <input
              v-model="sheets.sheet_url"
              class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-solid-2 text-n-slate-12 focus:outline-none focus:border-n-brand font-mono"
              placeholder="https://docs.google.com/spreadsheets/d/..."
            />
          </div>

          <div class="flex gap-2 mt-3">
            <button
              class="flex-1 bg-n-brand text-white rounded-lg py-2 text-sm font-medium hover:bg-n-brand/90 disabled:opacity-50 transition-colors"
              :disabled="isSheetsSaving"
              @click="saveSheets"
            >
              {{ isSheetsSaving ? 'Salvando...' : 'Salvar' }}
            </button>
            <button
              class="px-4 py-2 border border-n-weak rounded-lg text-sm text-n-slate-11 hover:bg-n-alpha-1 disabled:opacity-50 transition-colors flex items-center gap-1.5"
              :disabled="isSheetsTesting || !sheetsStatus.configured"
              @click="testSheets"
            >
              <span :class="isSheetsTesting ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-download'" class="text-sm" />
              {{ isSheetsTesting ? 'Importando...' : 'Importar agora' }}
            </button>
          </div>

          <div
            v-if="sheetsTestResult"
            class="mt-3 flex items-start gap-2 px-3 py-2.5 rounded-lg text-sm"
            :class="sheetsTestResult.success
              ? 'bg-green-500/10 text-green-700 border border-green-500/20'
              : 'bg-red-500/10 text-red-700 border border-red-500/20'"
          >
            <span
              class="text-base flex-shrink-0 mt-0.5"
              :class="sheetsTestResult.success ? 'i-lucide-check-circle' : 'i-lucide-alert-circle'"
            />
            {{ sheetsTestResult.message }}
          </div>

          <!-- Prévia das primeiras linhas -->
          <div v-if="sheetsPreview?.length" class="mt-3 border border-n-weak rounded-xl overflow-hidden">
            <p class="text-[11px] font-medium text-n-slate-10 px-3 pt-2.5 pb-1">Prévia (primeiras linhas):</p>
            <div class="overflow-x-auto">
              <table class="w-full text-xs">
                <thead>
                  <tr class="text-left text-n-slate-10">
                    <th class="px-3 py-1.5 font-medium">Data</th>
                    <th class="px-3 py-1.5 font-medium">Nome</th>
                    <th class="px-3 py-1.5 font-medium">Procedimento</th>
                    <th class="px-3 py-1.5 font-medium">Valor</th>
                    <th class="px-3 py-1.5 font-medium">Unidade</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, i) in sheetsPreview" :key="i" class="border-t border-n-weak text-n-slate-12">
                    <td class="px-3 py-1.5 whitespace-nowrap">{{ row.date || '—' }}</td>
                    <td class="px-3 py-1.5">{{ row.name || '—' }}</td>
                    <td class="px-3 py-1.5">{{ row.procedure || '—' }}</td>
                    <td class="px-3 py-1.5 whitespace-nowrap">{{ row.value || '—' }}</td>
                    <td class="px-3 py-1.5">{{ row.unit || '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <p v-if="sheetsStatus.fetched_at" class="text-[11px] text-n-slate-9 mt-2">
            Última importação: {{ new Date(sheetsStatus.fetched_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) }}
            — {{ sheetsStatus.cached_count }} linha(s) em uso no Dashboard.
          </p>
        </div>

        <!-- OftalmoFácil (conexão nativa) -->
        <div v-if="showSection('oftalmofacil')" :class="props.only ? '' : 'border-t border-n-weak pt-5'">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style="background: rgba(69, 181, 170, 0.12)">
              <span class="i-lucide-eye text-lg" style="color: #45B5AA" />
            </div>
            <div class="min-w-0">
              <h3 class="text-sm font-semibold text-n-slate-12">OftalmoFácil</h3>
              <p class="text-xs text-n-slate-10">Conexão nativa — endereço e chave da API do sistema OftalmoFácil</p>
            </div>
            <span
              class="ml-auto text-[10px] px-2 py-0.5 rounded-full font-medium flex-shrink-0"
              :class="oftalmoStatus.configured ? 'bg-green-500/15 text-green-600' : 'bg-n-alpha-2 text-n-slate-9'"
            >
              {{ oftalmoStatus.configured ? '✓ Conectada' : 'Pendente' }}
            </span>
          </div>

          <div class="bg-n-alpha-1 rounded-xl p-3.5 mb-4 text-xs text-n-slate-11 space-y-1.5">
            <p>
              A conexão é <b>direto no banco do OftalmoFácil</b> com um usuário <b>só-leitura</b> —
              nada é alterado lá. A cada 15 min o CEVICO lê as cirurgias do fornecedor
              (<b>{{ oftalmo.provider_name || 'CATARATA_SP' }}</b>), casa o paciente por telefone → CPF,
              põe o <b>valor no card</b>, retrodata a coluna e mostra a ficha completa no Espaço do Paciente (só admin).
            </p>
            <p class="text-n-slate-9">
              🛡️ Card que já está adiante nunca volta · realizada até 30 dias → "Cirurgia Realizada", mais antiga → "Pós Operatório" ·
              cancelada/ausente só ganham etiqueta · primeira carga é silenciosa (sem disparar automações).
            </p>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div class="col-span-2 sm:col-span-1">
              <label class="text-xs font-medium text-n-slate-11 block mb-1.5">Servidor do banco (host)</label>
              <input v-model="oftalmo.db_host" class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-solid-2 text-n-slate-12 font-mono" placeholder="ex.: 187.45.xx.xx ou mysql.dominio.com.br" />
            </div>
            <div>
              <label class="text-xs font-medium text-n-slate-11 block mb-1.5">Porta</label>
              <input v-model="oftalmo.db_port" class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-solid-2 text-n-slate-12 font-mono" placeholder="3306" />
            </div>
            <div>
              <label class="text-xs font-medium text-n-slate-11 block mb-1.5">Banco</label>
              <input v-model="oftalmo.db_name" class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-solid-2 text-n-slate-12 font-mono" placeholder="app_oftalmo" />
            </div>
            <div>
              <label class="text-xs font-medium text-n-slate-11 block mb-1.5">Usuário (só leitura)</label>
              <input v-model="oftalmo.db_user" class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-solid-2 text-n-slate-12 font-mono" placeholder="cevico_leitura" />
            </div>
            <div>
              <label class="text-xs font-medium text-n-slate-11 block mb-1.5">
                Senha <span v-if="oftalmoStatus.db_password_set" class="text-green-600 font-normal">(já configurada)</span>
              </label>
              <input v-model="oftalmo.db_password" type="password" class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-solid-2 text-n-slate-12 font-mono" :placeholder="oftalmoStatus.db_password_set ? 'Digite para substituir' : 'senha do usuário'" />
            </div>
            <div>
              <label class="text-xs font-medium text-n-slate-11 block mb-1.5">Fornecedor (a CEVICO lá dentro)</label>
              <input v-model="oftalmo.provider_name" class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-solid-2 text-n-slate-12 font-mono" placeholder="CATARATA_SP" />
            </div>
            <div class="flex items-center gap-2 pt-5">
              <button
                class="w-9 h-5 rounded-full transition-colors relative flex-shrink-0"
                :class="oftalmo.enabled ? 'bg-green-500' : 'bg-n-slate-6'"
                @click="oftalmo.enabled = !oftalmo.enabled"
              >
                <span class="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all" :class="oftalmo.enabled ? 'left-[18px]' : 'left-0.5'" />
              </button>
              <span class="text-xs text-n-slate-12">Sincronização automática {{ oftalmo.enabled ? 'ligada' : 'desligada' }}</span>
            </div>
          </div>

          <!-- de-para de médicos: lá o médico é só o CRM -->
          <div class="mt-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3">
            <div class="flex items-center gap-2 mb-1.5">
              <p class="text-xs font-medium text-n-slate-12">🩺 Médicos (CRM → nome)</p>
              <span class="text-[10px] text-n-slate-9">no OftalmoFácil o médico é só o número do CRM</span>
              <button class="ml-auto text-[11px] text-n-brand hover:underline" @click="addOftalmoDoctor">+ adicionar</button>
            </div>
            <div v-for="(d, di) in oftalmoDoctors" :key="di" class="grid grid-cols-[110px_1fr_auto] gap-2 mb-1.5">
              <input v-model="d.crm" class="border border-n-weak rounded-lg px-2 py-1.5 text-xs bg-n-solid-2 font-mono" placeholder="170937" />
              <input v-model="d.name" class="border border-n-weak rounded-lg px-2 py-1.5 text-xs bg-n-solid-2" placeholder="Dr. Nome Sobrenome" />
              <button class="i-lucide-x text-n-slate-9 hover:text-red-500 text-sm" @click="oftalmoDoctors.splice(di, 1)" />
            </div>
            <p v-if="!oftalmoDoctors.length" class="text-[11px] text-n-slate-9">Nenhum ainda — até preencher, a ficha mostra "CRM 170937".</p>
          </div>

          <!-- 🏥 item 228: o OftalmoFácil é um HUB de parceiros. CATARATA_SP = a CEVICO; o resto = parceiros -->
          <div class="mt-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3">
            <div class="flex items-center gap-2 mb-1.5">
              <p class="text-xs font-medium text-n-slate-12">🤝 Parceiros do hub</p>
              <span class="text-[10px] text-n-slate-9">o fornecedor acima é a CEVICO; todos os OUTROS fornecedores são parceiros de aquisição</span>
            </div>
            <label class="flex items-center gap-2 text-xs text-n-slate-12 mb-2">
              <input v-model="oftalmo.partners_enabled" type="checkbox" class="rounded" />
              Ler também os parceiros (sem isso, só as cirurgias do fornecedor da CEVICO entram)
            </label>
            <div class="grid grid-cols-2 gap-2">
              <label class="text-[11px] text-n-slate-11">
                Funil dos <b>parceiros</b> (cards de quem veio do hub)
                <select v-model="oftalmo.partner_pipeline_id" class="mt-1 w-full border border-n-weak rounded-lg px-2 py-1.5 text-xs bg-n-solid-2">
                  <option :value="null">— sem funil: fica só no espelho e na Agenda —</option>
                  <option v-for="p in crmPipelines" :key="'pp' + p.id" :value="p.id">{{ p.name }}</option>
                </select>
              </label>
              <label class="text-[11px] text-n-slate-11">
                Funil da <b>CEVICO</b> (cirurgias do nosso fornecedor)
                <select v-model="oftalmo.own_pipeline_id" class="mt-1 w-full border border-n-weak rounded-lg px-2 py-1.5 text-xs bg-n-solid-2">
                  <option :value="null">— o primeiro funil (padrão) —</option>
                  <option v-for="p in crmPipelines" :key="'op' + p.id" :value="p.id">{{ p.name }}</option>
                </select>
              </label>
            </div>
            <p class="text-[10px] text-n-slate-9 mt-1.5">Cada funil precisa ter as colunas "Cirurgia Agendada", "Cirurgia Realizada" e "Pós Operatório" (procura pelo nome, só dentro do funil escolhido). Contato de parceiro ganha as etiquetas <code>oftalmofacil</code> e <code>of_&lt;parceiro&gt;</code>.</p>
            <!-- 🚧 item 231: a CERCA — caixa(s) por onde a equipe fala com os pacientes dos parceiros -->
            <div class="mt-2 rounded-lg border border-amber-200 bg-amber-50/60 dark:bg-amber-900/10 dark:border-amber-800 p-2">
              <p class="text-[11px] font-medium text-n-slate-12">🚧 Cerca dos parceiros — caixa(s) por onde a equipe fala com esses pacientes</p>
              <p class="text-[10px] text-n-slate-9 mt-0.5">Paciente de parceiro (card no funil dos parceiros, etiqueta <code>of_…</code> ou conversa nessas caixas) <b>nunca</b> recebe agente de IA, robô de follow-up, automação de coluna, lembrete, jornada, campanha, régua, colheita nem ligação da IA — e não ganha card no funil da CEVICO nem conta como lead. Só gente fala com ele, por aqui.</p>
              <!-- 🧭 item 341: só leitura — a escolha mora em CRM → Ambientes do CRM -->
              <div class="flex flex-wrap gap-1.5 mt-1.5">
                <span v-for="ib in allInboxes.filter(i => oftalmo.partner_inbox_ids.includes(i.id))" :key="'pib' + ib.id" class="flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border border-amber-400 bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100">
                  <span class="i-lucide-inbox text-[10px]" /> {{ ib.name }}
                </span>
                <span v-if="!oftalmo.partner_inbox_ids.length" class="text-[10px] text-n-slate-9">nenhuma caixa na cerca</span>
              </div>
              <p class="text-[10px] text-n-slate-9 mt-1">Para trocar as caixas, use <b>CRM → Ambientes do CRM</b> (um número fica em um ambiente só).</p>
            </div>
            <div v-if="oftalmoPartnersSeen.length" class="mt-2 flex flex-wrap gap-1">
              <span v-for="p in oftalmoPartnersSeen" :key="'ps' + p.name" class="text-[10px] px-2 py-0.5 rounded-full border" :class="p.own ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-n-weak text-n-slate-11'" :title="p.own ? 'É a CEVICO' : 'Parceiro'">
                {{ p.own ? '⭐ ' : '' }}{{ p.name }} · {{ p.total }}
              </span>
            </div>
            <p v-else class="text-[10px] text-n-slate-9 mt-2">Clique em "Testar conexão" para ver quem são os parceiros que existem lá.</p>
          </div>

          <!-- 🏥 item 228: Agenda unificada -->
          <div class="mt-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3">
            <div class="flex items-center gap-2 mb-1.5">
              <p class="text-xs font-medium text-n-slate-12">📅 Agenda unificada</p>
              <span class="text-[10px] text-n-slate-9">cada cirurgia/exame/consulta do hub vira um agendamento na Agenda, com o selo de origem</span>
            </div>
            <div class="grid grid-cols-2 gap-2 items-end">
              <label class="flex items-center gap-2 text-xs text-n-slate-12">
                <input v-model="oftalmo.agenda_enabled" type="checkbox" class="rounded" />
                Criar agendamentos na Agenda
              </label>
              <label class="text-[11px] text-n-slate-11">
                Só a partir de (data do procedimento) — vale para a Agenda e para os PARCEIROS
                <input v-model="oftalmo.agenda_from" type="date" class="mt-1 w-full border border-n-weak rounded-lg px-2 py-1.5 text-xs bg-n-solid-2" />
              </label>
            </div>
            <p class="text-[10px] text-n-slate-9 mt-1.5">Parceiro com data ANTES dessa data fica só no espelho (não vira paciente, card nem agendamento); a CEVICO mantém o histórico inteiro. Sem data = segunda-feira desta semana. Acompanha o hub: mudou a data lá, muda aqui; realizada = concluída e "compareceu"; cancelada sai do calendário; ausente = "faltou". Entram como "Lançada" (não contam como agendamento novo). Hoje: <b>{{ oftalmoStatus.agenda_count || 0 }}</b> agendamento(s) na Agenda vindos do hub.</p>

            <div class="flex items-center gap-2 mt-3 mb-1.5">
              <p class="text-xs font-medium text-n-slate-12">📍 Clínicas do hub → local na Agenda</p>
              <button class="ml-auto text-[11px] text-n-brand hover:underline" @click="addOftalmoClinic()">+ adicionar</button>
            </div>
            <div v-for="(c, ci) in oftalmoClinics" :key="'cl' + ci" class="grid grid-cols-[1fr_1fr_auto] gap-2 mb-1.5">
              <input v-model="c.name" class="border border-n-weak rounded-lg px-2 py-1.5 text-xs bg-n-solid-2" placeholder="nome da clínica como aparece lá" />
              <select v-model="c.unit" class="border border-n-weak rounded-lg px-2 py-1.5 text-xs bg-n-solid-2">
                <option value="">— escolha o local —</option>
                <option v-for="u in oftalmoUnitOptions" :key="'uo' + u.key" :value="u.key">{{ u.label }}</option>
              </select>
              <button class="i-lucide-x text-n-slate-9 hover:text-red-500 text-sm" @click="oftalmoClinics.splice(ci, 1)" />
            </div>
            <div v-if="oftalmoClinicsSeen.length" class="flex flex-wrap gap-1 mt-1">
              <button v-for="c in oftalmoClinicsSeen" :key="'cs' + c.name" class="text-[10px] px-2 py-0.5 rounded-full border border-n-weak hover:bg-n-alpha-2" :class="clinicMapped(c.name) ? 'text-emerald-700 border-emerald-300' : 'text-n-slate-11'" :title="clinicMapped(c.name) ? 'já mapeada' : 'clique para adicionar ao de-para'" @click="!clinicMapped(c.name) && addOftalmoClinic(c.name)">
                {{ clinicMapped(c.name) ? '✓ ' : '+ ' }}{{ c.name }} · {{ c.total }}
              </button>
            </div>
            <p v-if="oftalmoTypesSeen.length" class="text-[10px] text-n-slate-9 mt-2">
              Tipos de procedimento lá: <span v-for="(t, ti) in oftalmoTypesSeen" :key="'ty' + t.name">{{ ti ? ' · ' : '' }}{{ t.name }} ({{ t.total }})</span>. "Exame" vai para o trilho Exames, "Consulta" para Consultas, o resto para Cirurgias.
            </p>
          </div>

          <div class="flex gap-2 mt-3 flex-wrap">
            <button class="flex-1 text-white rounded-lg py-2 text-sm font-medium disabled:opacity-50 transition-colors" style="background: #45B5AA" :disabled="isOftalmoSaving" @click="saveOftalmo">
              {{ isOftalmoSaving ? 'Salvando...' : 'Salvar conexão' }}
            </button>
            <button class="px-3 rounded-lg py-2 text-sm font-medium border border-n-weak text-n-slate-11 hover:bg-n-alpha-1 disabled:opacity-50" :disabled="isOftalmoTesting || !oftalmoStatus.configured" @click="testOftalmo">
              {{ isOftalmoTesting ? 'Testando…' : '🔌 Testar conexão' }}
            </button>
            <button class="px-3 rounded-lg py-2 text-sm font-medium border border-n-weak text-n-slate-11 hover:bg-n-alpha-1 disabled:opacity-50" :disabled="isOftalmoSyncing || !oftalmoStatus.configured" @click="syncOftalmo(false)">
              {{ isOftalmoSyncing ? 'Iniciando…' : '🔄 Sincronizar agora' }}
            </button>
          </div>
          <p v-if="oftalmoTest" class="mt-2 text-xs" :class="oftalmoTest.ok ? 'text-emerald-600' : 'text-red-500'">
            <template v-if="oftalmoTest.ok">✅ Conectou! <b>{{ oftalmoTest.total }}</b> cirurgia(s) do fornecedor lá<template v-if="oftalmoTest.first_date"> desde {{ oftalmoTest.first_date }}</template>.</template>
            <template v-else>❌ {{ oftalmoTest.error }}</template>
          </p>
          <!-- Rodada 4 (achado #382): a sincronização falhou ou parou → faixa vermelha, em vez de a tela seguir mostrando a última rodada boa -->
          <div v-if="oftalmoStatus.sync_alert" class="mt-2 text-[11px] rounded-lg px-3 py-2 bg-red-500/10 border border-red-500/30 text-red-600">
            <b>{{ oftalmoStatus.sync_alert.kind === 'erro' ? '❌ A sincronização falhou' : '⏸️ A sincronização parou' }}</b>
            <template v-if="oftalmoStatus.sync_alert.at"> ({{ new Date(oftalmoStatus.sync_alert.at).toLocaleString('pt-BR') }})</template>
            · {{ oftalmoStatus.sync_alert.message }}
            <span v-if="oftalmoStatus.last_error && oftalmoStatus.sync_alert.kind === 'erro'" class="block mt-1 font-mono opacity-80">{{ oftalmoStatus.last_error }}</span>
          </div>
          <div v-if="oftalmoStatus.last_run_at" class="mt-2 text-[11px] text-n-slate-10 rounded-lg bg-n-alpha-1 px-3 py-2">
            Última sincronização: {{ new Date(oftalmoStatus.last_run_at).toLocaleString('pt-BR') }} ·
            <b>{{ oftalmoStatus.last_result?.pulled || 0 }}</b> lidas · <b>{{ oftalmoStatus.last_result?.moved || 0 }}</b> cards movidos ·
            <b>{{ oftalmoStatus.last_result?.created_contacts || 0 }}</b> pacientes criados · <b>{{ oftalmoStatus.last_result?.ahead || 0 }}</b> preservados 🛡️ ·
            <b>{{ oftalmoStatus.mirror_count || 0 }}</b> cirurgias espelhadas no total
            <template v-if="oftalmoStatus.last_result?.tasks_created || oftalmoStatus.last_result?.tasks_updated"> · Agenda: <b>{{ oftalmoStatus.last_result.tasks_created || 0 }}</b> criados, <b>{{ oftalmoStatus.last_result.tasks_updated || 0 }}</b> atualizados</template>
            <template v-if="oftalmoStatus.last_result?.partners"> · <b>{{ oftalmoStatus.last_result.partners }}</b> de parceiros</template>
            <span v-if="oftalmoStatus.last_result?.skipped_partners" class="text-amber-600"> · {{ oftalmoStatus.last_result.skipped_partners }} de parceiro sem funil (escolha o funil dos parceiros)</span>
            <span v-if="oftalmoStatus.last_result?.error_count" class="text-red-500"> · {{ oftalmoStatus.last_result.error_count }} erro(s): {{ (oftalmoStatus.last_result.errors || [])[0] }}</span>
            <button class="ml-2 text-n-brand hover:underline" @click="syncOftalmo(true)">recarregar tudo do zero</button>
          </div>

          <p v-if="oftalmoStatus.updated_at" class="text-[11px] text-n-slate-9 mt-2">
            Última alteração: {{ new Date(oftalmoStatus.updated_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) }}
          </p>
        </div>

      </div>
    </div>
  </div>
</template>
